import { Component, Inject, ViewChild } from '@angular/core';
import { NgProgress } from '@ngx-progressbar/core';
import { SelectComponent } from 'ng2-select';
import { ToastrService } from 'ngx-toastr';
import { APIOperableComponent } from '../../../../shared/components/api-operable/api-operable.component';
import { GridSettings } from '../../../../shared/model/grid-settings.interface';
import { UIActiveDialogService } from '../../../../shared/services/ui-dialog.service';
import { SortUtils } from '../../../../shared/utils/sort.utils';
import { MakeModel } from '../../../make/model/make.model';
import { AuthService } from '../../../security/service/auth.service';
import { ProductSearchModel } from '../../model/product-search.model';
import { ProductUIMessages } from '../../model/product-ui.messages';
import { ProductModel } from '../../model/product.model';
import { ProductService } from '../../service/product.service';

@Component({
  selector: 'product-deactivate-component',
  templateUrl:
    './console/app-js/modules/product/components/deactivate/product-deactivate.component.html'
})
export class ProductDeactivateComponent extends APIOperableComponent {
  @ViewChild('makeModelSelect', { static: true })
  protected makeModelSelect: SelectComponent;
  protected searchModel = new ProductSearchModel();
  protected products: Array<ProductModel> = [];
  protected productSelected: Array<any> = [];
  protected operations: any = {
    loadingProducts: false
  };
  private productMakes: Array<MakeModel> = [];
  private originalData: Array<ProductModel>;
  protected filterValue: string;
  protected filteredMakes: Array<MakeModel>;
  protected makeFilterSelected: MakeModel;
  protected productsToDeactivate: Array<any>;
  protected hideResultsMsg = true;
  public noRecordsSettings = {
    message: 'No Results Found',
    styleClass: 'no-records-wrapper hide'
  };
  protected gridSettings: GridSettings = {
    state: {
      skip: 0,
      take: 5,
      filter: {
        logic: 'and',
        filters: []
      }
    },
    columnsConfig: [
      {
        label: 'Make',
        type: 'string',
        property: 'make',
        isVisible: true,
        width: 100
      },
      {
        label: 'Model',
        type: 'string',
        property: 'model',
        isVisible: true,
        width: 100
      }
    ],
    realColWidths: []
  };

  constructor(
    @Inject('products') products: Array<any>,
    progressService: NgProgress,
    toastr: ToastrService,
    authService: AuthService,
    private productService: ProductService,
    private activeDialog: UIActiveDialogService
  ) {
    super(progressService, toastr, authService);
    this.productsToDeactivate = [...products];
    this.filteredMakes = [];
    this.originalData = [];
    this.filterValue = '';
    this.onLoad();
  }

  /**
   * Load all lists from API for the view.
   * @param {ProductModel} product
   */
  private onLoad(): void {
    this.searchModel.filterModel.type = 'Machine';
    this.searchModel.pageSize = 100;
    this.loadProductMakes();
  }

  /**
   * On Deactivate button click event, send associate keywords flag to false.
   */
  protected okButton() {
    this.activeDialog.close({ associateKeywords: false });
  }

  /**
   * On Deactivate Product With Keywords, send associate keywords flag to true
   */
  protected deactivateProductWithKeywords() {
    this.activeDialog.close({
      associateKeywords: true,
      productsSelected: this.productSelected
    });
  }

  /**
   * On Cancel button clock event, dimiss the window.
   */
  protected cancelCloseButton(): void {
    this.activeDialog.dismiss();
  }

  /**
   * Call to API to Search Products based on search model.
   * @param filterByMake
   */
  private onSearch(filterByMake = false): void {
    this.progressRef.start();
    this.operations.loadingProducts = true;

    if (filterByMake) {
      this.products = [];
      this.searchModel.pageNumber = 1;
    }
    this.productService.searchPartAndProduct(this.searchModel).subscribe(
      (res: any) => {
        this.searchModel = this.productService.getSearchResultModel(
          this.searchModel,
          res
        );
        if (res.responseCode === 200 && res.results) {
          this.searchModel.results = res.results;
          this.products = this.products.concat(res.results);
          this.products = SortUtils.sortCollection(this.products, 'model');
          this.products = this.removeProductsToDeactivateFromSearchResults(
            this.products,
            this.productsToDeactivate
          );
          this.searchAndSelectProduct();
        } else {
          this.searchModel.results = [];
          this.products = [];
          this.onAPIError(
            res.errorMessage
              ? res.errorMessage
              : ProductUIMessages.SEARCH_PRODUCTS_ERROR
          );
        }
        this.originalData = [...this.products];
        this.onFilterData();
        this.operations.loadingProducts = false;
        this.noRecordsSettings.styleClass =
          this.products.length > 0
            ? 'no-records-wrapper hide'
            : 'no-records-wrapper';
        this.progressRef.complete();
      },
      () => this.onAPIError(ProductUIMessages.SEARCH_PRODUCTS_ERROR)
    );
  }

  /**
   * Removes products to deactivate from this makes list and returns the new list.
   * @param products: Array<ProductModel>
   * @param productsToDeactivate: Array<any>
   */
  private removeProductsToDeactivateFromSearchResults(
    products: Array<ProductModel>,
    productsToDeactivate: Array<any>
  ): Array<ProductModel> {
    const ids = productsToDeactivate.map(item => item.id);
    return products.filter(item => {
      return ids.findIndex(id => id === item.id) === -1;
    });
  }

  /**
   * Loads the list of product makes.
   */
  private loadProductMakes(): void {
    this.productService.getProductMakes().subscribe(
      (res: any) => {
        if (res.responseCode === 200) {
          this.productMakes = res.results;
          this.productMakes = this.productMakes.sort((a, b) =>
            SortUtils.compareByProperty(a, b, 'name')
          );
          this.filteredMakes = [...this.productMakes];
        } else {
          this.onAPIError('An error ocurred while loading product makes');
        }
        this.onSearch();
      },
      error => this.onAPIError('An error ocurred while loading product makes')
    );
  }

  /**
   * Search for a product on the current data set and adds it to the current productSelection.
   */
  private searchAndSelectProduct(): void {
    if (this.productSelected[0] && this.productSelected[0].id) {
      const match = this.products.find(
        item => item.id === this.productSelected[0].id
      );
      if (match) {
        this.productSelected = [];
        this.productSelected.push(match);
      }
    }
  }

  /**
   * Get Make by id from the current list of makes previously loaded.
   * @param {number} makeId
   * @returns {MakeModel}
   */
  private getMake(makeId: number): MakeModel {
    return this.productMakes.find(item => item.id === makeId);
  }

  /**
   * On page change event.
   */
  onPaginationChange(): void {
    this.onSearch(true);
  }

  /**
   * Clear the input filter.
   */
  onClearFilter(): void {
    this.filterValue = '';
    this.onFilterData();
  }

  /**
   * Load next page of products on scroll to end of list
   */
  infinityScroll() {
    if (
      !this.operations.loadingProducts &&
      this.searchModel.totalPages > this.searchModel.pageNumber
    ) {
      this.searchModel.pageNumber += 1;
      this.onSearch(false);
    }
  }

  /**
   * Executes filter when user type some value.
   */
  onFilterData(): void {
    if (!this.originalData) {
      this.originalData = [...this.products];
    }
    let value = this.filterValue.toLocaleLowerCase();
    // filter our data
    const temp = this.originalData.filter(r => {
      if (typeof r.model !== 'undefined') {
        return r.model.toLowerCase().indexOf(value) !== -1 || !value;
      }
    });
    // update the rows
    this.products = temp;
  }

  /**
   * This function will handle the text input by the user on the Make combobox.
   * @param textInput
   */
  handleFilter(textInput: string): void {
    this.filteredMakes = this.productMakes.filter(
      (make: MakeModel) =>
        make.name.toLowerCase().indexOf(textInput.toLowerCase()) !== -1
    );
  }

  /**
   * When selected Make filter change, do search.
   */
  onMakeSelectedChange(): void {
    if (this.makeFilterSelected) {
      this.searchModel.filterModel.make = this.makeFilterSelected.name;
    } else {
      this.searchModel.filterModel.make = null;
      this.handleFilter('');
    }
    this.onSearch(true);
  }

  /**
   * Update selected rows to associate keywords
   */
  selectedRowChange(event): void {
    event.selectedRows.map(item => {
      this.productSelected.push(item.dataItem);
    });
    if (event.deselectedRows.length) {
      this.productSelected = this.productSelected.filter(product => {
        return !event.deselectedRows.some(deselectedItem => {
          return product.makeId === deselectedItem.dataItem.makeId;
        });
      });
    }
  }
}
